import { Button, Col, Form, Input, Row, Select, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { Cheat, User } from '../api/go';

type DayCount = 7 | 30;

interface QueryForm {
  userId: string;
  dayCount: DayCount;
}

interface TeammateTableRow {
  key: string;
  user_id: string;
  nick_name: string;
  teammate_count: number;
  status?: number;
  disable_remark: string;
}

const columns: ColumnsType<TeammateTableRow> = [
  {
    title: 'UserId',
    dataIndex: 'user_id',
    key: 'user_id',
    width: 220,
  },
  {
    title: '昵称',
    dataIndex: 'nick_name',
    key: 'nick_name',
    width: 180,
  },
  {
    title: '组队次数',
    dataIndex: 'teammate_count',
    key: 'teammate_count',
    width: 120,
    sorter: (left, right) => right.teammate_count - left.teammate_count,
  },
  {
    title: '账号状态',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    render: (value?: number) => {
      let text = '未知';

      if (value === 0) {
        text = '封禁';
      } else if (value === 1) {
        text = '正常';
      }

      return <div style={value === 0 ? { color: 'red', fontWeight: 'bold' } : {}}>{text}</div>;
    },
  },
  {
    title: '封号备注',
    dataIndex: 'disable_remark',
    key: 'disable_remark',
    render: (_, record) => <div style={{ textAlign: 'left' }}>{record.status === 0 ? record.disable_remark || '-' : '-'}</div>,
  },
];

function sortRows(rows: TeammateTableRow[]) {
  return rows.sort((left, right) => {
    if (left.teammate_count !== right.teammate_count) {
      return right.teammate_count - left.teammate_count;
    }

    return left.user_id.localeCompare(right.user_id);
  });
}

function formatRatio(numerator: number, denominator: number) {
  if (denominator === 0) {
    return '0.00%';
  }

  return `${((numerator / denominator) * 100).toFixed(2)}%`;
}

export default function CheatUserTeammateCount() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<TeammateTableRow[]>([]);
  const [queryMeta, setQueryMeta] = useState<{
    userId: string;
    dayCount: DayCount;
    rawTotal: number;
  } | null>(null);

  async function handleQuery(values: QueryForm) {
    const userId = values.userId.trim();
    const dayCount = values.dayCount;
    if (!userId) {
      message.error('请输入 user_id');
      return;
    }

    setLoading(true);
    try {
      const teammateCountMap = await Cheat.userTeammateCount(userId, dayCount);
      const teammateRows = Object.entries(teammateCountMap).map(([teammateUserId, teammateCount]) => ({
        key: teammateUserId,
        user_id: teammateUserId,
        nick_name: '-',
        teammate_count: teammateCount,
        status: undefined,
        disable_remark: '',
      }));
      const filteredRows = teammateRows.filter((row) => row.teammate_count > 3);

      if (teammateRows.length === 0) {
        setQueryMeta({
          userId,
          dayCount,
          rawTotal: 0,
        });
        setRows([]);
        return;
      }

      if (filteredRows.length === 0) {
        setQueryMeta({
          userId,
          dayCount,
          rawTotal: teammateRows.length,
        });
        setRows([]);
        return;
      }

      const userInfoList = await User.infoList(
        filteredRows.map((row) => row.user_id),
      );
      const userInfoMap: Record<string, (typeof userInfoList)[0]> = {};

      for (const userInfo of userInfoList) {
        userInfoMap[userInfo.UserId] = userInfo;
      }

      const mergedRows = filteredRows.map((row) => ({
        ...row,
        nick_name: userInfoMap[row.user_id]?.NickName || '-',
        status: userInfoMap[row.user_id]?.Status,
        disable_remark: userInfoMap[row.user_id]?.DisableRemark || '',
      }));

      setQueryMeta({
        userId,
        dayCount,
        rawTotal: teammateRows.length,
      });
      setRows(sortRows(mergedRows));
    } catch {
      setQueryMeta(null);
      setRows([]);
      message.error('查询失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }

  const bannedCount = rows.filter((row) => row.status === 0).length;
  const banRatio = formatRatio(bannedCount, rows.length);

  return (
    <div>
      <Row>
        <Col span={10}>
          <Form<QueryForm>
            name="userTeammateCount"
            layout="horizontal"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            style={{ maxWidth: 720 }}
            onFinish={handleQuery}
            autoComplete="off"
            initialValues={{ dayCount: 30 }}
          >
            <Form.Item<QueryForm> label="User ID" name="userId" required>
              <Input placeholder="请输入 user_id" disabled={loading} />
            </Form.Item>
            <Form.Item<QueryForm> label="时间范围" name="dayCount" required>
              <Select
                disabled={loading}
                options={[
                  { label: '最近 7 天', value: 7 },
                  { label: '最近 30 天', value: 30 },
                ]}
              />
            </Form.Item>
            <Form.Item label="过滤规则">
              <Typography.Text>仅展示组队次数大于 3 的玩家</Typography.Text>
            </Form.Item>
            <Form.Item label={null}>
              <Button type="primary" htmlType="submit" loading={loading}>
                查询
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>

      {queryMeta ? (
        <div style={{ marginBottom: 16, textAlign: 'left' }}>
          <Typography.Text>
            查询用户 {queryMeta.userId}，范围最近 {queryMeta.dayCount} 天，原始共 {queryMeta.rawTotal} 个队友，过滤后 {rows.length} 个，被封号 {bannedCount} 个，占比 {banRatio}
          </Typography.Text>
        </div>
      ) : null}

      <Table<TeammateTableRow>
        rowKey="user_id"
        loading={loading}
        dataSource={rows}
        columns={columns}
        scroll={{ x: 900 }}
        pagination={false}
      />
    </div>
  );
}
